import { useMemo } from "react";
import { parseMoodTimestamp } from "utils/common/timestampUtils";
import { isSameDay } from "utils/common/weekUtils";
import { getWeekdayDates } from "./weekdayUtils";
import type { Mood } from "../../../types/models";
import type { WeekInfo } from "./useWeekManagement";

export interface MoodPercentages {
  Apathy: number;
  Flow: number;
  Anxiety: number;
  Confident: number;
}

export function useMoodPercentages(
  moods: Mood[],
  selectedView: "PERSONAL" | "TEAM",
  selectedWeekData: WeekInfo | null,
  availableWeeks: WeekInfo[],
  lastEndedWeek: Date[],
  insightsGranularity: "week" | "month" = "week"
): MoodPercentages {
  const moodPercentages = useMemo(() => {
    const totalMoodDurations: Record<string, number> = {
      Apathy: 0,
      Flow: 0,
      Anxiety: 0,
      Confident: 0,
    };

    if (insightsGranularity === "month") {
      moods.forEach((mood: Mood) => {
        const duration = parseInt(String(mood.duration)) || 1;
        const moodType = mood.mood;
        if (
          Object.prototype.hasOwnProperty.call(totalMoodDurations, moodType)
        ) {
          totalMoodDurations[moodType] += duration;
        }
      });
    } else {
      const weekdayDates = getWeekdayDates(
        selectedView,
        selectedWeekData,
        availableWeeks,
        lastEndedWeek
      );

      weekdayDates.forEach((weekdayDate) => {
        const dayMoods = moods.filter((mood: Mood) => {
          const moodDate = parseMoodTimestamp(mood);
          if (!moodDate) return false;
          return isSameDay(moodDate, weekdayDate);
        });

        dayMoods.forEach((mood: Mood) => {
          const duration = parseInt(String(mood.duration)) || 1;
          const moodType = mood.mood;
          if (
            Object.prototype.hasOwnProperty.call(totalMoodDurations, moodType)
          ) {
            totalMoodDurations[moodType] += duration;
          }
        });
      });
    }

    const grandTotal = Object.values(totalMoodDurations).reduce(
      (sum, val) => sum + val,
      0
    );

    if (grandTotal === 0) {
      return {
        Apathy: 25,
        Flow: 25,
        Anxiety: 25,
        Confident: 25,
      };
    }

    const rawPercentages = {
      Apathy: Math.round((totalMoodDurations.Apathy / grandTotal) * 100),
      Flow: Math.round((totalMoodDurations.Flow / grandTotal) * 100),
      Anxiety: Math.round((totalMoodDurations.Anxiety / grandTotal) * 100),
      Confident: Math.round((totalMoodDurations.Confident / grandTotal) * 100),
    };

    const sum =
      rawPercentages.Apathy +
      rawPercentages.Flow +
      rawPercentages.Anxiety +
      rawPercentages.Confident;
    const diff = 100 - sum;

    const percentages = { ...rawPercentages };
    if (diff !== 0) {
      const largestKey = Object.entries(percentages).reduce((a, b) =>
        percentages[a[0] as keyof typeof percentages] >
        percentages[b[0] as keyof typeof percentages]
          ? a
          : b
      )[0] as keyof typeof percentages;
      percentages[largestKey] += diff;
    }

    return percentages;
  }, [
    moods,
    selectedView,
    selectedWeekData,
    availableWeeks,
    lastEndedWeek,
    insightsGranularity,
  ]);

  return moodPercentages;
}
